import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FullscreenLayout } from '@/components/Layout/FullscreenLayout';
import { EventSelector } from './components/EventSelector/EventSelector';
import { EventSwitchModal } from './components/EventSelector/EventSwitchModal';
import { ContextSidebar } from './components/ContextSidebar/ContextSidebar';
import { StepNavigation } from './components/WizardSteps/StepNavigation';
import { RPAgentPanel } from './RPAgentPanel';
import { Button } from '@/components/ui/Button';
import { Bot } from 'lucide-react';
import { ResearchStep } from './components/WizardSteps/Step1Research/ResearchStep';
import { RFPReviewStep } from './components/WizardSteps/Step2RFPs/RFPReviewStep';
import { SelectionStep } from './components/WizardSteps/Step3Selection/SelectionStep';
import { ProposalStep } from './components/WizardSteps/Step4Proposal/ProposalStep';
import { ResearchService } from '@/services/ResearchService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { ClientsService, type ClientDTO } from '@/services/ClientsService';

type Step = 1 | 2 | 3 | 4;

const STORAGE_KEY_PREFIX = 'research-proposal-step';
const SIDEBAR_STORAGE_KEY = 'research-proposal-sidebar-open';

function getStorageKey(eventId: string): string {
  return `${STORAGE_KEY_PREFIX}-${eventId}`;
}

function loadPersistedStep(eventId: string): Step | null {
  try {
    const saved = localStorage.getItem(getStorageKey(eventId));
    if (saved) {
      const step = parseInt(saved, 10) as Step;
      if ([1, 2, 3, 4].includes(step)) {
        return step;
      }
    }
  } catch (error) {
    console.error('Failed to load persisted step:', error);
  }
  return null;
}

function savePersistedStep(eventId: string, step: Step): void {
  try {
    localStorage.setItem(getStorageKey(eventId), step.toString());
  } catch (error) {
    console.error('Failed to save persisted step:', error);
  }
}

function loadPersistedSidebarState(): boolean {
  try {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (saved !== null) {
      return saved === 'true';
    }
  } catch (error) {
    console.error('Failed to load persisted sidebar state:', error);
  }
  return true; // Default to open
}

function savePersistedSidebarState(isOpen: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, isOpen.toString());
  } catch (error) {
    console.error('Failed to save persisted sidebar state:', error);
  }
}

export function ResearchProposalTool() {
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  
  // Initialize state from localStorage if eventId is available
  const getInitialStep = (): Step => {
    if (eventId) {
      const persistedStep = loadPersistedStep(eventId);
      if (persistedStep) {
        return persistedStep;
      }
    }
    return 1;
  };
  
  const initialStep = getInitialStep();
  const loadedFromStorage = eventId && loadPersistedStep(eventId) !== null;
  const initialSidebarOpen = loadPersistedSidebarState();
  
  const [currentStep, setCurrentStep] = useState<Step>(initialStep);
  // ── R&P Manager agent surface (env-gated like the manifest agent's launch
  //    switch): VITE_RP_AGENT_ENABLED=true builds it in (staging); absent →
  //    the tool is byte-identical to today. ?ai=1 also opens it for testing.
  const RP_AGENT_ENABLED = import.meta.env.VITE_RP_AGENT_ENABLED === 'true'
    || new URLSearchParams(window.location.search).get('ai') === '1';
  const [agentOpen, setAgentOpen] = useState(
    RP_AGENT_ENABLED && new URLSearchParams(window.location.search).get('agent') === '1',
  );
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const [showEventSwitchModal, setShowEventSwitchModal] = useState(false);
  const skipNextSave = useRef(loadedFromStorage); // Skip save on initial mount if we loaded from storage
  const lastEventId = useRef<string | undefined>(eventId);
  
  const researchService = new ResearchService();
  const eventsService = new EventsService();
  const clientsService = new ClientsService();
  
  // Check if shortlisted venues exist to determine if step 2 is accessible
  const { data: shortlistData } = useQuery({
    queryKey: ['research-proposal-shortlist', eventId],
    queryFn: () => researchService.getAllShortlistedVenuesForEvent(eventId!),
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
  });
  
  // Check if submitted proposals exist to determine if step 3 is accessible
  const { data: submittedProposalsData } = useQuery({
    queryKey: ['research-proposal-submitted', eventId],
    queryFn: () => researchService.getSubmittedProposals(eventId!, 'all'),
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
  });
  
  // Fetch event data
  const { data: eventData } = useQuery({
    queryKey: ['research-proposal-event', eventId],
    queryFn: () => eventsService.get(eventId!),
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
  });
  
  // Fetch client data
  const { data: clientData } = useQuery({
    queryKey: ['research-proposal-client', eventData?.client_id],
    queryFn: () => clientsService.get(eventData!.client_id),
    enabled: !!eventData?.client_id,
    staleTime: 30000, // 30 seconds
  });
  
  const hasShortlistedVenues = (shortlistData?.total_venues ?? 0) > 0;
  const hasSubmittedProposals = (submittedProposalsData?.totals?.submitted ?? 0) > 0;
  const hasApprovedVenues = (submittedProposalsData?.totals?.approved ?? 0) > 0;
  
  // Load persisted step when eventId changes (but not on initial mount, handled by useState initializer)
  useEffect(() => {
    if (eventId && eventId !== lastEventId.current) {
      const persistedStep = loadPersistedStep(eventId);
      if (persistedStep) {
        skipNextSave.current = true; // Don't save when we're loading from localStorage
        setCurrentStep(persistedStep);
      }
      lastEventId.current = eventId;
    }
  }, [eventId]);
  
  // Save step to localStorage when it changes (skip when loading from localStorage)
  useEffect(() => {
    if (eventId && !skipNextSave.current) {
      savePersistedStep(eventId, currentStep);
    }
    // Reset the skip flag after the effect runs
    if (skipNextSave.current) {
      skipNextSave.current = false;
    }
  }, [eventId, currentStep]);
  
  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    savePersistedSidebarState(sidebarOpen);
  }, [sidebarOpen]);
  
  // Determine which steps are accessible
  const accessibleSteps: Step[] = [1]; // Step 1 is always accessible
  if (hasShortlistedVenues) {
    accessibleSteps.push(2);
  }
  if (hasSubmittedProposals) {
    accessibleSteps.push(3);
  }
  // Step 4 (Proposal) is accessible when at least 1 venue is approved
  if (hasApprovedVenues) {
    accessibleSteps.push(4);
  }

  const handleEventSelect = (selectedEventId: string) => {
    navigate(`/tools/research-proposal/${selectedEventId}`);
  };

  const handleEventSwitch = (newEventId: string) => {
    navigate(`/tools/research-proposal/${newEventId}`);
  };

  // If no event selected, show event selector
  if (!eventId) {
    return (
      <FullscreenLayout
        title="Open Neo"
        subtitle="AI-powered venue research and proposal workflow"
      >
        <EventSelector onEventSelect={handleEventSelect} />
      </FullscreenLayout>
    );
  }

  if (agentOpen && RP_AGENT_ENABLED && eventId) {
    return <RPAgentPanel eventId={eventId} onClose={() => setAgentOpen(false)} />;
  }

  return (
    <FullscreenLayout
      eventName={eventData?.name}
      clientName={clientData?.name}
      onEventSwitchClick={() => setShowEventSwitchModal(true)}
    >
      <div className="h-full flex">
        {/* Context Sidebar - Mobile: Fixed overlay, Desktop: Slides in/out of layout */}
        <div
          className={`
            fixed lg:relative inset-y-0 left-0 z-30
            transform transition-all duration-300 ease-in-out
            overflow-hidden
            ${sidebarOpen ? 'translate-x-0 lg:w-96' : '-translate-x-full lg:translate-x-0 lg:w-0'}
            w-full sm:w-96
          `}
        >
          <div className="h-full w-full sm:w-96">
            <ContextSidebar
              eventId={eventId}
              onClose={() => setSidebarOpen(false)}
              onSwitchEventClick={() => setShowEventSwitchModal(true)}
            />
          </div>
        </div>

        {/* Event Switch Modal - Rendered at top level for full screen overlay */}
        <EventSwitchModal
          isOpen={showEventSwitchModal}
          onClose={() => setShowEventSwitchModal(false)}
          onEventSelect={handleEventSwitch}
        />

        {/* Main Content Area - Expands when sidebar is closed on desktop */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Step Navigation with Sidebar Toggle */}
          <div className="border-b bg-white px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <StepNavigation 
                currentStep={currentStep} 
                onStepChange={setCurrentStep}
                accessibleSteps={accessibleSteps}
              />
            </div>
            {RP_AGENT_ENABLED && (
              // Design-system Button (secondary = Moniker teal). The first cut used
              // raw bg-teal-700, but the theme defines `teal` as a single color,
              // not a scale, so the class generated nothing: white text on a
              // transparent background, invisible on the white header.
              <Button variant="secondary" onClick={() => setAgentOpen(true)} className="flex-shrink-0 gap-2">
                <Bot className="w-4 h-4" /> View Agent
              </Button>
            )}
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-auto">
            {currentStep === 1 && (
              <ResearchStep 
                eventId={eventId} 
                onContinueToRFPs={() => setCurrentStep(2)}
              />
            )}
            {currentStep === 2 && (
              <RFPReviewStep 
                eventId={eventId} 
                onBack={() => setCurrentStep(1)}
                onContinueToSelection={() => setCurrentStep(3)}
                submittedProposalCount={submittedProposalsData?.totals?.submitted ?? 0}
              />
            )}
            {currentStep === 3 && (
              <SelectionStep 
                eventId={eventId} 
                onBack={() => setCurrentStep(2)}
                onContinueToProposal={() => setCurrentStep(4)}
              />
            )}
            {currentStep === 4 && (
              <ProposalStep 
                eventId={eventId} 
                onBack={() => setCurrentStep(3)}
              />
            )}
          </div>
        </div>

        {/* Mobile sidebar toggle button (floating) */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed bottom-6 right-6 w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform hover:scale-110"
          aria-label="Toggle sidebar"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {sidebarOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>
    </FullscreenLayout>
  );
}

